import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Icon, IconButton, useDialogBehavior } from "@astrum/ui";
import { api, ApiError } from "../lib/api.js";
import { parseCsv, previewKindFor } from "../lib/file-preview.js";
import { downloadFile, fileUrl, isDesktop, openFile } from "../lib/transport.js";
import { useFileUrl } from "../lib/useFileUrl.js";
import "./document-viewer.css";
import "./file-viewer.css";

type PreviewData =
  | { kind: "text"; contentType: string; text: string; truncated: boolean }
  | { kind: "table"; sheetName: string | null; rows: string[][]; truncated: boolean };

export type ViewedFile = { id: string; fileName: string; contentType: string };

function Table({ rows }: { rows: string[][] }) {
  const [head, ...body] = rows;
  if (!head) return <p className="file-viewer__note">This file is empty.</p>;
  return (
    <div className="file-viewer__table-wrap">
      <table className="file-viewer__table">
        <thead>
          <tr>
            {head.map((cell, i) => (
              <th key={i}>{cell}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {body.map((row, r) => (
            <tr key={r}>
              {row.map((cell, c) => (
                <td key={c}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Viewer for everything the image lightbox and PDF viewer do not cover:
 * video/audio (native players over /preview, which supports Range), text, CSV
 * and xlsx as text/tables via /preview-data, and a Download/Open card for
 * Office documents (no server-side conversion).
 */
export function FileViewer({ file, onClose }: { file: ViewedFile | null; onClose: () => void }) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const open = file !== null;
  const kind = file ? previewKindFor(file.contentType) : null;
  const mediaSrc = useFileUrl(file && (kind === "video" || kind === "audio") ? `/api/v1/files/${file.id}/preview` : null);

  const [data, setData] = useState<PreviewData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useDialogBehavior(open, onClose, containerRef);

  const needsData = kind === "text" || kind === "csv" || kind === "xlsx";
  const fileId = file?.id;
  useEffect(() => {
    setData(null);
    setError(null);
    if (!fileId || !needsData) return;
    let cancelled = false;
    api
      .get<PreviewData>(`/files/${fileId}/preview-data`)
      .then((res) => !cancelled && setData(res))
      .catch((e: unknown) => !cancelled && setError(e instanceof ApiError ? e.message : "Could not load the preview."));
    return () => {
      cancelled = true;
    };
  }, [fileId, needsData]);

  const csvRows = useMemo(() => (data?.kind === "text" && kind === "csv" ? parseCsv(data.text) : null), [data, kind]);

  if (!file) return null;
  const downloadPath = `/api/v1/files/${file.id}/download`;
  const openIt = () => {
    if (isDesktop) void openFile(downloadPath, file.fileName);
    else window.open(fileUrl(downloadPath), "_blank", "noopener");
  };

  return (
    <div
      className="document-viewer"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div ref={containerRef} className="document-viewer__frame" role="dialog" aria-modal="true" aria-label={file.fileName} tabIndex={-1}>
        <header className="document-viewer__head">
          <Icon name="document" size={16} />
          <span className="document-viewer__name" title={file.fileName}>
            {file.fileName}
          </span>
          <Button size="sm" variant="secondary" icon={<Icon name="download" size={14} />} onClick={() => void downloadFile(downloadPath, file.fileName)}>
            Download
          </Button>
          <IconButton icon={<Icon name="close" size={18} />} label="Close" onClick={onClose} />
        </header>
        <div className="file-viewer__body">
          {kind === "video" && mediaSrc && <video className="file-viewer__video" src={mediaSrc} controls preload="metadata" />}
          {kind === "audio" && mediaSrc && <audio className="file-viewer__audio" src={mediaSrc} controls preload="metadata" />}
          {kind === "office" && (
            <div className="file-viewer__card">
              <Icon name="document" size={32} />
              <strong>{file.fileName}</strong>
              <p>Office documents cannot be previewed here.</p>
              <div className="file-viewer__card-actions">
                <Button variant="primary" onClick={() => void downloadFile(downloadPath, file.fileName)}>
                  Download
                </Button>
                <Button variant="secondary" onClick={openIt}>
                  Open
                </Button>
              </div>
            </div>
          )}
          {needsData && error && <p className="file-viewer__note" role="alert">{error}</p>}
          {needsData && !error && !data && <p className="file-viewer__note">Loading preview…</p>}
          {data?.kind === "text" && csvRows && <Table rows={csvRows} />}
          {data?.kind === "text" && !csvRows && <pre className="file-viewer__text">{data.text}</pre>}
          {data?.kind === "table" && (
            <>
              {data.sheetName && <p className="file-viewer__note">Sheet: {data.sheetName}</p>}
              <Table rows={data.rows} />
            </>
          )}
          {data?.truncated && <p className="file-viewer__note">Preview truncated — download the file for the rest.</p>}
        </div>
      </div>
    </div>
  );
}
